import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../util/firebase';
import { ref, onValue, push, remove, set } from 'firebase/database';

export default function SimpleCalendar({ todoList = {} }) {
  const { currentUser } = useAuth();
  const [events, setEvents] = useState({});
  const [accountTodos, setAccountTodos] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateInput, setDateInput] = useState('');
  const [selectedTodo, setSelectedTodo] = useState('');
  const [entryName, setEntryName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [persistent, setPersistent] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const eventsRef = ref(db, `users/${currentUser.uid}/calendar`);
    const unsubscribeEvents = onValue(eventsRef, (snap) => {
      setEvents(snap.val() || {});
    });
    const todosRef = ref(db, `users/${currentUser.uid}/todoList`);
    const unsubscribeTodos = onValue(todosRef, (snap) => {
      setAccountTodos(snap.val() || {});
    });
    return () => {
      unsubscribeEvents();
      unsubscribeTodos();
    };
  }, [currentUser]);

  const availableTodos = Object.keys(todoList).length > 0 ? todoList : accountTodos;

  const addEvent = async () => {
    if (!currentUser) return alert('Please sign in to add calendar events.');
    if (!dateInput) return alert('Please choose a date.');

    const todo = availableTodos[selectedTodo];
    const title = entryName.trim() || (todo && todo.title) || 'Untitled';

    try {
      // If no todo was selected, create a todo so the task appears in the TodoList
      let sourceId = selectedTodo || null;
      if (!sourceId) {
        const todoRef = ref(db, `users/${currentUser.uid}/todoList`);
        const newTodoRef = push(todoRef);
        // create todo and mark as calendar-origin; calendarEventId will be set after we create the event
        const createdAt = new Date().toISOString();
        await set(newTodoRef, {
          title,
          createdAt,
          order: Date.now(),
          isCalendar: true,
          persistent: persistent || false
        });
        sourceId = newTodoRef.key;
      } else {
        // existing todo linked: mark it as calendar-backed
        try {
          await set(ref(db, `users/${currentUser.uid}/todoList/${sourceId}/isCalendar`), true);
          await set(ref(db, `users/${currentUser.uid}/todoList/${sourceId}/persistent`), persistent || false);
        } catch (e) {
          console.warn('Could not mark linked todo as calendar item', e);
        }
      }

      const eventData = {
        title,
        date: dateInput,
        sourceId: sourceId,
        createdAt: new Date().toISOString(),
        order: Date.now(),
        persistent: persistent || false,
      };

      const eventsRef = ref(db, `users/${currentUser.uid}/calendar`);
      const newEventRef = push(eventsRef);
      await set(newEventRef, eventData);

      // write back the calendarEventId onto the todo so TodoList can deduplicate and track linkage
      if (sourceId) {
        try {
          await set(ref(db, `users/${currentUser.uid}/todoList/${sourceId}/calendarEventId`), newEventRef.key);
          // ensure the isCalendar flag also exists (in case it was an existing todo)
          await set(ref(db, `users/${currentUser.uid}/todoList/${sourceId}/isCalendar`), true);
          // ensure persistent flag is present on the todo as well
          await set(ref(db, `users/${currentUser.uid}/todoList/${sourceId}/persistent`), persistent || false);
        } catch (e) {
          console.warn('Could not attach calendarEventId to todo', e);
        }
      }

      setEntryName('');
      setSelectedTodo('');
      setDateInput('');
      setPersistent(false);
      setShowModal(false);
    } catch (e) {
      console.error(e);
      alert('Error adding event');
    }
  };

  const deleteEvent = async (id) => {
    if (!currentUser) return;
    try {
      const eventRef = ref(db, `users/${currentUser.uid}/calendar/${id}`);
      await remove(eventRef);
    } catch (e) {
      console.error(e);
      alert('Error deleting event');
    }
  };

  const exportToICS = () => {
    const items = Object.entries(events);
    if (!items.length) return alert('No events to export');

    const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Run Do It//SimpleCalendar//EN'];
    items.forEach(([id, ev]) => {
      const dt = (ev.date || '').replace(/-/g, '');
      const next = new Date((ev.date || '') + 'T00:00:00');
      next.setDate(next.getDate() + 1);
      const dtend = `${next.getFullYear()}${String(next.getMonth()+1).padStart(2,'0')}${String(next.getDate()).padStart(2,'0')}`;

      lines.push('BEGIN:VEVENT');
      lines.push(`UID:${id}@rundoit`);
      lines.push(`DTSTAMP:${new Date().toISOString().replace(/[-:]/g,'').split('.')[0]}Z`);
      lines.push(`DTSTART;VALUE=DATE:${dt}`);
      lines.push(`DTEND;VALUE=DATE:${dtend}`);
      lines.push(`SUMMARY:${(ev.title || '').replace(/\n/g,'\\n')}`);
      lines.push('END:VEVENT');
    });
    lines.push('END:VCALENDAR');

    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rundoit-calendar-${new Date().toISOString().split('T')[0]}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth()+1, 0).getDate();

  const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const startDay = startOfMonth.getDay();
  const totalDays = daysInMonth(currentDate);

  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));

  const eventsByDate = Object.entries(events).reduce((acc, [id, ev]) => {
    acc[ev.date] = acc[ev.date] || [];
    acc[ev.date].push({ id, ...ev });
    return acc;
  }, {});

  return (
    <div style={{maxWidth:900,margin:'88px auto 24px',padding:16,background:'#fff',borderRadius:8,border:'1px solid #e6e6e6'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{fontWeight:700}}>{currentDate.toLocaleString(undefined,{month:'long',year:'numeric'})}</div>
        <div>
          <button onClick={prevMonth} style={{marginRight:8}}>◀</button>
          <button onClick={nextMonth}>▶</button>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:6,marginBottom:12}}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} style={{textAlign:'center',fontWeight:700}}>{d}</div>
        ))}

        {/* empty cells */}
        {Array.from({length:startDay}).map((_,i)=> (
          <div key={`empty-${i}`} style={{minHeight:80,opacity:0.4}} />
        ))}

        {Array.from({length:totalDays}).map((_,i) => {
          const day = i+1;
          const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth()+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const dayEvents = eventsByDate[dateKey] || [];

          return (
            <div key={dateKey} onClick={() => { setDateInput(dateKey); setShowModal(true); }} style={{minHeight:80,padding:8,border:'1px solid #f0f0f0',position:'relative',cursor:'pointer'}}>
              <div style={{fontWeight:700,marginBottom:6}}>{day}</div>
              {dayEvents.map(ev => (
                <div key={ev.id} style={{background:ev.completed ? '#64748b' : '#0078d4',color:'#fff',padding:'4px 6px',borderRadius:6,fontSize:12,marginBottom:6,display:'flex',justifyContent:'space-between',alignItems:'center',opacity:ev.archived ? 0.55 : 1}}>
                  <span style={{marginRight:8,textDecoration:ev.completed ? 'line-through' : 'none'}}>{ev.title}</span>
                  <button onClick={(e) => { e.stopPropagation(); deleteEvent(ev.id); }} style={{background:'transparent',border:'none',color:'#fff',cursor:'pointer'}}>✕</button>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Modal for adding event when a date is clicked */}
      {showModal && (
        <div onClick={() => setShowModal(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999}}>
          <div onClick={(e)=>e.stopPropagation()} style={{background:'#fff',padding:20,borderRadius:8,width:'90%',maxWidth:480}}>
            <h3 style={{marginTop:0}}>Add event for {dateInput}</h3>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:12}}>
              <label style={{fontSize:13}}>Entry Name
                <input type="text" value={entryName} onChange={(e)=>setEntryName(e.target.value)} placeholder="Optional, will use todo title if empty" style={{width:'100%',padding:8,marginTop:6}} />
              </label>

              <label style={{fontSize:13}}>Link Todo (optional)
                <select value={selectedTodo} onChange={(e)=>setSelectedTodo(e.target.value)} style={{width:'100%',padding:8,marginTop:6}}>
                  <option value="">-- none --</option>
                  {Object.entries(availableTodos).filter(([, task]) => task?.isCalendar !== true && !task?.completed && !task?.archived).map(([id,t])=> (
                    <option key={id} value={id}>{t.title}</option>
                  ))}
                </select>
              </label>

              <label style={{fontSize:13,display:'flex',alignItems:'center',gap:8}}>
                <input type="checkbox" checked={persistent} onChange={(e)=>setPersistent(e.target.checked)} />
                <span>Repeat / keep after selection (persistent)</span>
              </label>
            </div>

            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={() => { setShowModal(false); }} style={{padding:'8px 12px',border:'1px solid #e6e6e6',borderRadius:6}}>Cancel</button>
              <button onClick={() => { /* ensure dateInput is set */ addEvent(); }} style={{padding:'8px 12px',background:'#0078d4',color:'#fff',border:'none',borderRadius:6}}>Save</button>
            </div>
          </div>
        </div>
      )}

      <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'center'}}>
        <label style={{display:'flex',flexDirection:'column',fontSize:13}}>
          Date
          <input type="date" value={dateInput} onChange={(e)=>setDateInput(e.target.value)} />
        </label>

        <label style={{display:'flex',flexDirection:'column',fontSize:13}}>
          Link Todo (optional)
          <select value={selectedTodo} onChange={(e)=>setSelectedTodo(e.target.value)}>
            <option value="">-- none --</option>
            {Object.entries(availableTodos).filter(([, task]) => task?.isCalendar !== true && !task?.completed && !task?.archived).map(([id,t])=> (
              <option key={id} value={id}>{t.title}</option>
            ))}
          </select>
        </label>

        <label style={{display:'flex',flexDirection:'column',fontSize:13}}>
          Entry Name
          <input type="text" placeholder="Optional, will use todo title if empty" value={entryName} onChange={(e)=>setEntryName(e.target.value)} />
        </label>

        <label style={{display:'flex',alignItems:'center',gap:8}}>
          <input type="checkbox" checked={persistent} onChange={(e)=>setPersistent(e.target.checked)} />
          <span style={{fontSize:13}}>Repeat / keep after selection</span>
        </label>

        <div style={{display:'flex',gap:8,alignItems:'flex-end'}}>
          <button onClick={addEvent} style={{padding:'8px 12px',background:'#0078d4',color:'#fff',border:'none',borderRadius:6}}>Add to Calendar</button>
          <button onClick={exportToICS} style={{padding:'8px 12px',border:'1px solid #e6e6e6',borderRadius:6}}>Export .ics</button>
        </div>
      </div>
    </div>
  );
}
